import { toTypedSchema } from "@vee-validate/yup";
import * as yup from "yup";
import { countryConfigsMap } from "@/core/constants/address-country-configs";
import { usePostalCodeValidation } from "@/shared/thorlabs/shared/composables/usePostalCodeValidation";

export const ADDRESS_FIELD_LIMIT = 35;
export const BUILDING_FIELD_LIMIT = 25;
export const MAIL_STOP_FIELD_LIMIT = 9;
export const EMAIL_FIELD_LIMIT = 50;

type TranslateFnType = (key: string, params?: unknown[]) => string;
type PostalCodeValidatorType = (code: string, countryCode: string) => { valid: boolean; example?: string };

export interface IAddressSchemaOptions {
  t: TranslateFnType;
  withPersonalInfo?: boolean;
  requiredEmail?: boolean;
  requiredPhone?: boolean;
  requiredCity?: boolean;
  countryId?: string;
  countryName?: string;
  countryCode?: string;
  countries?: { id: string; regions: { id: string }[] }[];
  validatePostalCode?: PostalCodeValidatorType;
}

export function createEmailSchema(options: Pick<IAddressSchemaOptions, "t" | "withPersonalInfo" | "requiredEmail">) {
  const { t, withPersonalInfo, requiredEmail } = options;
  let rules = yup.string().trim().max(EMAIL_FIELD_LIMIT, t("common.messages.character_limit_50")).email().nullable();
  if (withPersonalInfo && requiredEmail) {
    rules = rules.required();
  }
  return rules;
}

export function createPhoneSchema(options: Pick<IAddressSchemaOptions, "t" | "withPersonalInfo" | "requiredPhone">) {
  const { t, withPersonalInfo, requiredPhone } = options;
  const regex = /^[0-9 ()+-/]*$/;
  let rules = yup
    .string()
    .trim()
    .nullable()
    .matches(regex, { message: t("common.messages.invalid_field_regex") })
    .test("phone-length", t("common.messages.character_limit_15"), (value) => {
      if (!value) {
        return true;
      }
      const nonFormattingChars = value.replace(/[ ()+-/]/g, "");
      return nonFormattingChars.length <= 15;
    });

  if (withPersonalInfo && requiredPhone) {
    rules = rules.required();
  }
  return rules;
}

export function createNameSchema(options: Pick<IAddressSchemaOptions, "t" | "withPersonalInfo">) {
  const { t, withPersonalInfo } = options;
  let rules = yup.string().trim().max(ADDRESS_FIELD_LIMIT, t("common.messages.character_limit_35")).nullable();
  if (withPersonalInfo) {
    rules = rules.required();
  }
  return rules;
}

export const createFirstNameSchema = createNameSchema;
export const createLastNameSchema = createNameSchema;

export function createOrganizationSchema(options: Pick<IAddressSchemaOptions, "countryId">) {
  const { countryId } = options;
  let rules = yup.string().max(128).nullable();
  if (countryId === "JPN") {
    rules = rules.required();
  }
  return rules;
}

export function createCitySchema(
  options: Pick<IAddressSchemaOptions, "t" | "requiredCity"> & { isCityVisible?: boolean },
) {
  const { t, requiredCity, isCityVisible = true } = options;
  let rules = yup.string().trim().max(ADDRESS_FIELD_LIMIT, t("common.messages.character_limit_35")).nullable();
  if (requiredCity && isCityVisible) {
    rules = rules.required();
  }
  return rules;
}

export function createRegionSchema(options: Pick<IAddressSchemaOptions, "countries"> & { isRegionVisible?: boolean }) {
  const { countries = [], isRegionVisible = true } = options;
  if (!isRegionVisible) {
    return yup.string().nullable();
  }
  return yup
    .string()
    .nullable()
    .when("countryCode", {
      is: (value: string) => countries.find((item) => value === item.id)?.regions.length,
      then: (schema) => schema.required(),
    });
}

export function createPostalCodeSchema(
  options: Pick<IAddressSchemaOptions, "t" | "validatePostalCode" | "countryCode" | "countryName">,
) {
  const { t, validatePostalCode, countryCode, countryName } = options;
  return yup
    .string()
    .trim()
    .max(ADDRESS_FIELD_LIMIT, t("common.messages.character_limit_35"))
    .required()
    .nullable()
    .test("postal-code-format", "", (value, context) => {
      if (!value || value.trim() === "") {
        return true;
      }
      if (!validatePostalCode) {
        return true;
      }
      const result = validatePostalCode(value, countryCode ?? "");
      if (!result.valid) {
        return context.createError({
          message: t("common.messages.invalid_postal_code_format", [result.example, countryName]),
        });
      }
      return true;
    });
}

export function createLine1Schema(options: Pick<IAddressSchemaOptions, "t">) {
  const { t } = options;
  return yup.string().trim().max(ADDRESS_FIELD_LIMIT, t("common.messages.character_limit_35")).required().nullable();
}

export function createCountryCodeSchema() {
  return yup.string().required().nullable();
}

export function createMaxLengthSchema(limit: number, message: string) {
  return yup.string().max(limit, message).nullable();
}

export function createTrimmedMaxLengthSchema(limit: number, message: string) {
  return yup.string().trim().max(limit, message).nullable();
}

export function createBooleanSchema() {
  return yup.boolean();
}

export interface IAddressObjectSchemaOptions {
  t: TranslateFnType;
  withPersonalInfo?: boolean;
  requiredEmail?: boolean;
  requiredPhone?: boolean;
  requiredCity?: boolean;
  countries?: { id: string; regions: { id: string }[] }[];
}

/**
 * Creates a yup object schema that validates an entire address object at once.
 * Use this for standalone validation (e.g. validating a selected address in checkout)
 * without requiring a vee-validate form context.
 */
export function createAddressObjectSchema(options: IAddressObjectSchemaOptions) {
  const { t, withPersonalInfo, requiredEmail, requiredPhone, requiredCity, countries } = options;
  const { validatePostalCode } = usePostalCodeValidation();

  return yup.object({
    firstName: createNameSchema({ t, withPersonalInfo }),
    lastName: createNameSchema({ t, withPersonalInfo }),
    email: createEmailSchema({ t, withPersonalInfo, requiredEmail }),
    phone: createPhoneSchema({ t, withPersonalInfo, requiredPhone }),
    organization: yup
      .string()
      .max(128)
      .nullable()
      .when("countryCode", {
        is: "JPN",
        then: (schema) => schema.required(),
      }),
    line1: createLine1Schema({ t }),
    line2: createMaxLengthSchema(ADDRESS_FIELD_LIMIT, t("common.messages.character_limit_35")),
    city: yup
      .string()
      .trim()
      .max(ADDRESS_FIELD_LIMIT, t("common.messages.character_limit_35"))
      .nullable()
      .when("countryCode", {
        is: (value: string) => {
          const config = countryConfigsMap[value] || countryConfigsMap["DEFAULT"];
          return requiredCity && !!config.cityLabel;
        },
        then: (schema) => schema.required(),
      }),
    countryCode: createCountryCodeSchema(),
    regionId: yup
      .string()
      .nullable()
      .when("countryCode", {
        is: (value: string) => {
          const config = countryConfigsMap[value] || countryConfigsMap["DEFAULT"];
          if (!config.stateLabel) {
            return false;
          }
          return !!countries?.find((item) => value === item.id)?.regions.length;
        },
        then: (schema) => schema.required(),
      }),
    postalCode: yup
      .string()
      .trim()
      .max(ADDRESS_FIELD_LIMIT, t("common.messages.character_limit_35"))
      .required()
      .nullable()
      .test("postal-code-format", "", function (value) {
        if (!value || value.trim() === "") {
          return true;
        }
        if (!validatePostalCode) {
          return true;
        }
        const parentCountryCode = (this.parent as { countryCode?: string }).countryCode ?? "";
        const parentCountryName = (this.parent as { countryName?: string }).countryName ?? parentCountryCode;
        const result = validatePostalCode(value, parentCountryCode);
        if (!result.valid) {
          return this.createError({
            message: t("common.messages.invalid_postal_code_format", [result.example, parentCountryName]),
          });
        }
        return true;
      }),
    departmentTL: createMaxLengthSchema(ADDRESS_FIELD_LIMIT, t("common.messages.character_limit_35")),
    buildingTL: createMaxLengthSchema(BUILDING_FIELD_LIMIT, t("common.messages.character_limit_25")),
    mailStopTL: createMaxLengthSchema(MAIL_STOP_FIELD_LIMIT, t("common.messages.character_limit_9")),
  });
}

// Typed schema variants (pre-wrapped with toTypedSchema for vee-validate)
export const createEmailTypedSchema = (...args: Parameters<typeof createEmailSchema>) =>
  toTypedSchema(createEmailSchema(...args));
export const createPhoneTypedSchema = (...args: Parameters<typeof createPhoneSchema>) =>
  toTypedSchema(createPhoneSchema(...args));
export const createNameTypedSchema = (...args: Parameters<typeof createNameSchema>) =>
  toTypedSchema(createNameSchema(...args));
export const createFirstNameTypedSchema = createNameTypedSchema;
export const createLastNameTypedSchema = createNameTypedSchema;
export const createOrganizationTypedSchema = (...args: Parameters<typeof createOrganizationSchema>) =>
  toTypedSchema(createOrganizationSchema(...args));
export const createCityTypedSchema = (...args: Parameters<typeof createCitySchema>) =>
  toTypedSchema(createCitySchema(...args));
export const createRegionTypedSchema = (...args: Parameters<typeof createRegionSchema>) =>
  toTypedSchema(createRegionSchema(...args));
export const createPostalCodeTypedSchema = (...args: Parameters<typeof createPostalCodeSchema>) =>
  toTypedSchema(createPostalCodeSchema(...args));
export const createLine1TypedSchema = (...args: Parameters<typeof createLine1Schema>) =>
  toTypedSchema(createLine1Schema(...args));
export const createCountryCodeTypedSchema = () => toTypedSchema(createCountryCodeSchema());
export const createMaxLengthTypedSchema = (...args: Parameters<typeof createMaxLengthSchema>) =>
  toTypedSchema(createMaxLengthSchema(...args));
export const createTrimmedMaxLengthTypedSchema = (...args: Parameters<typeof createTrimmedMaxLengthSchema>) =>
  toTypedSchema(createTrimmedMaxLengthSchema(...args));
export const createBooleanTypedSchema = () => toTypedSchema(createBooleanSchema());

export const addressSchemas = {
  ADDRESS_FIELD_LIMIT,
  BUILDING_FIELD_LIMIT,
  MAIL_STOP_FIELD_LIMIT,
  createEmailTypedSchema,
  createPhoneTypedSchema,
  createFirstNameTypedSchema,
  createLastNameTypedSchema,
  createOrganizationTypedSchema,
  createCityTypedSchema,
  createRegionTypedSchema,
  createPostalCodeTypedSchema,
  createLine1TypedSchema,
  createCountryCodeTypedSchema,
  createMaxLengthTypedSchema,
  createTrimmedMaxLengthTypedSchema,
  createBooleanTypedSchema,
};
